import { useEffect, useMemo, useState } from "react";
import { EyeIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import Card from "../shared/Card/Card";
import LoadingWheel from "../shared/LoadingWheel";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import CustomTinyMceEditor from "../shared/Inputs/CustomTinyMceEditor";
import useTemplateEditor from "../../data/useTemplateEditor";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { ITemplateEditorParentTemplate, TemplateType } from "../../models/TemplateEditor";
import TemplatePreviewModal, { ITemplatePreview } from "./TemplatePreviewModal";

interface ITemplateDraft {
  title: string;
  body: string;
}

const getTemplateTypeLabel = (type: TemplateType) => {
  switch (type) {
    case TemplateType.Email:
      return "Email";
    case TemplateType.Message:
      return "SMS";
    default:
      return "Template";
  }
};

export default function AdminTemplates() {
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers("Template");
  const {
    templateEditor,
    templates,
    availableParents,
    createChildTemplate,
    updateChildTemplate,
    resetChildTemplate,
    deleteChildTemplate,
  } = useTemplateEditor();

  const [selectedParentId, setSelectedParentId] = useState("");
  const [drafts, setDrafts] = useState<Record<string, ITemplateDraft>>({});
  const [preview, setPreview] = useState<ITemplatePreview | null>(null);

  // The saved version that actually sends: the custom copy if there is one, otherwise the
  // default. Unsaved edits in the editor are deliberately not shown.
  const openPreview = (template: ITemplateEditorParentTemplate) => {
    const child = template.childTemplate;
    setPreview({
      title: `${child?.title ?? template.title} (${getTemplateTypeLabel(template.type)})`,
      type: template.type,
      body: child?.body ?? template.body,
      isCustom: Boolean(child),
    });
  };

  useEffect(() => {
    const nextDrafts: Record<string, ITemplateDraft> = {};
    templates.forEach((template) => {
      if (!template.childTemplate) return;
      nextDrafts[template.childTemplate.id] = {
        title: template.childTemplate.title,
        body: template.childTemplate.body,
      };
    });
    setDrafts(nextDrafts);
  }, [templates]);

  const selectedParent = useMemo(() => {
    return availableParents.find((template) => template.id === selectedParentId);
  }, [availableParents, selectedParentId]);

  const updateDraft = (childTemplateId: string, patch: Partial<ITemplateDraft>) => {
    setDrafts((previous) => ({
      ...previous,
      [childTemplateId]: {
        ...previous[childTemplateId],
        ...patch,
      },
    }));
  };

  const handleCreateChild = async () => {
    if (!selectedParent) return;

    try {
      await createChildTemplate.mutateAsync({
        parentTemplateId: selectedParent.id,
        request: {
          type: selectedParent.type,
          title: selectedParent.title,
          body: selectedParent.body,
          parentTemplateId: selectedParent.id,
        },
      });
      setSelectedParentId("");
      saveHandlers.onSuccess();
    } catch {
      saveHandlers.onError();
    }
  };

  const handleSaveChild = async (template: ITemplateEditorParentTemplate) => {
    if (!template.childTemplate) return;

    const draft = drafts[template.childTemplate.id] || {
      title: template.childTemplate.title,
      body: template.childTemplate.body,
    };

    try {
      await updateChildTemplate.mutateAsync({
        childTemplateId: template.childTemplate.id,
        request: {
          type: template.type,
          title: draft.title,
          body: draft.body,
          parentTemplateId: template.id,
        },
      });
      saveHandlers.onSuccess();
    } catch {
      saveHandlers.onError();
    }
  };

  const handleResetChild = async (template: ITemplateEditorParentTemplate) => {
    if (!template.childTemplate) return;

    try {
      await resetChildTemplate.mutateAsync(template.childTemplate.id);
      saveHandlers.onSuccess();
    } catch {
      saveHandlers.onError();
    }
  };

  const handleRemoveChild = async (template: ITemplateEditorParentTemplate) => {
    if (!template.childTemplate) return;

    try {
      await deleteChildTemplate.mutateAsync(template.childTemplate.id);
      deleteHandlers.onSuccess();
    } catch {
      deleteHandlers.onError();
    }
  };

  if (templateEditor.isLoading) {
    return <LoadingWheel />;
  }

  return (
    <div>
      <h1 className="mb-2">Email & SMS Templates</h1>
      <p className="text-sm text-gray-500 mb-4">
        Customise the emails and text messages sent on your behalf. Add a custom copy of a default template to edit it;
        your copy is used in place of the default. Reset it to start again from the default, or remove it to go back to the default.
      </p>

      {Boolean(templateEditor.error) && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          Failed to load templates
        </div>
      )}

      <Card title="Add Custom Template" containerClassName="mb-6" bodyClassName="p-6">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="w-full sm:max-w-md">
            <label className="block text-sm font-medium text-gray-700 mb-1">Default Template</label>
            <select
              className="w-full rounded-md border border-gray-300 text-sm"
              value={selectedParentId}
              onChange={(e) => setSelectedParentId(e.target.value)}
            >
              <option value="">Select template...</option>
              {availableParents.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.title} ({getTemplateTypeLabel(template.type)})
                </option>
              ))}
            </select>
          </div>
          <ButtonInput
            isSubmit={false}
            label={createChildTemplate.isLoading ? "Adding..." : "Add Custom Template"}
            onClick={handleCreateChild}
            disabled={!selectedParentId || createChildTemplate.isLoading}
            classes="py-2.5"
          />
        </div>
      </Card>

      {templates.map((template) => {
        const mode = template.type === TemplateType.Message ? "text" : "html";
        const child = template.childTemplate;
        const draft = child ? (drafts[child.id] || { title: child.title, body: child.body }) : null;
        const isChildMutating =
          (updateChildTemplate.isLoading && updateChildTemplate.variables?.childTemplateId === child?.id) ||
          (resetChildTemplate.isLoading && resetChildTemplate.variables === child?.id) ||
          (deleteChildTemplate.isLoading && deleteChildTemplate.variables === child?.id);

        return (
          <Card
            key={template.id}
            title={`${template.title} (${getTemplateTypeLabel(template.type)})`}
            containerClassName="mb-6"
            bodyClassName="p-6"
          >
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-3 sm:items-start">
                <div className="flex-1">
                  {template.description && (
                    <div className="flex gap-2 text-sm text-blue-800 bg-blue-50 border border-blue-100 rounded-md px-3 py-2">
                      <InformationCircleIcon className="h-5 w-5 flex-shrink-0 text-blue-500" aria-hidden="true" />
                      <p>{template.description}</p>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => openPreview(template)}
                  className="self-end sm:self-start inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50"
                >
                  <EyeIcon className="h-4 w-4" aria-hidden="true" />
                  Preview
                </button>
              </div>
              {child ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Custom Template Title</label>
                    <input
                      type="text"
                      value={draft?.title || ""}
                      onChange={(e) => updateDraft(child.id, { title: e.target.value })}
                      className="w-full rounded-md border border-gray-300 text-sm"
                    />
                  </div>

                  <CustomTinyMceEditor
                    content={draft?.body || ""}
                    setContent={(value) => updateDraft(child.id, { body: value })}
                    mode={mode}
                    mergeTags={template.mergeTags}
                    placeholder={`Edit ${getTemplateTypeLabel(template.type).toLowerCase()} template...`}
                    height={420}
                  />

                  <div className="flex flex-wrap gap-3 justify-end">
                    <ButtonInput
                      isSubmit={false}
                      label="Remove Custom Template"
                      onClick={() => handleRemoveChild(template)}
                      disabled={isChildMutating}
                      classes="bg-red-600 hover:bg-red-700"
                    />
                    <ButtonInput
                      isSubmit={false}
                      label="Reset To Default"
                      onClick={() => handleResetChild(template)}
                      disabled={isChildMutating}
                      classes="bg-gray-600 hover:bg-gray-700"
                    />
                    <ButtonInput
                      isSubmit={false}
                      label="Save Custom Template"
                      onClick={() => handleSaveChild(template)}
                      disabled={isChildMutating}
                    />
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-500">No custom template created yet - the default is being used.</p>
              )}
            </div>
          </Card>
        );
      })}

      <TemplatePreviewModal preview={preview} onClose={() => setPreview(null)} />
    </div>
  );
}
